'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiMagazine} from '@/types/strapi';
import parse from 'html-react-parser';
import {useLocale} from 'next-intl';
import Link from 'next/link';
import {useSearchParams} from 'next/navigation';
import {useEffect, useState} from 'react';
import DateBlock from '../tools/DateBlock';
import {LazyImage} from '../tools/LazyImage';
import ComingSoon from './ComingSoon';
import {LoadingItem} from './LoadingItem';
import Pagination from './Pagination';

export default function WatsonMagazineList() {
  const locale = useLocale();
  const [searchResults, setSearchResults] = useState<StrapiMagazine[]>([]);
  const searchParams = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('p') || '1');
  });
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const maxDescriptionLength = {
    en: 50,
    zh: 150
  }[locale];

  const descriptionHandler = (content: string | null) => {
    if (!content) {
      return '';
    }
    const words = content
      .split(' ')
      .filter((str) => str)
      .join(' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ' ')
      .split(/\s+/);
    if (locale === 'en') {
      return (
        words.slice(0, maxDescriptionLength).join(' ') +
        (maxDescriptionLength < words.length ? '...' : '')
      );
    } else {
      return (
        words.join('').substring(0, maxDescriptionLength) +
        (maxDescriptionLength < content.length ? '...' : '')
      );
    }
  };

  const getSearchResult = async () => {
    setIsLoading(true);
    const postData = await getStrapiData(locale, 'magazines', {
      filters: {
        posted_at: {
          $lte: new Date()
        }
      },
      populate: ['thumbnail', 'file'],
      pagination: {
        page: currentPage,
        pageSize: 10
      },
      sort: ['posted_at:desc']
    });
    const totalPages = postData?.meta?.pagination?.pageCount || 1;
    setSearchResults(postData?.data || []);
    setTotalPages(totalPages);
    setIsLoading(false);
  };

  useEffect(() => {
    getSearchResult();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('p', currentPage.toString());
    window.history.pushState({}, '', url.href);
    getSearchResult();
  }, [currentPage]);

  return (
    <div className="questrial text-[18px]">
      <div>
        {!isLoading && searchResults.length === 0 && <ComingSoon />}
        <div className={cn('flex flex-col divider divide-y divide-gray-200 ')}>
          {isLoading && (
            <LoadingItem>
              <div className="flex flex-col ">
                <div className="my-4 flex flex-cols-2 gap-4 md:items-start w-full">
                  <div className="w-1/3 md:w-1/4 lg:w-1/5 ">
                    <div className="object-cover object-center w-full flex justify-center items-center ">
                      <div className="w-full">
                        <LazyImage className="aspect-[3/2] w-full rounded-lg" />
                      </div>
                    </div>
                  </div>
                  <div className="text-gray-600 w-2/3 md:w-3/4 lg:w-4/5 flex items-start ">
                    <div className="w-full">
                      <div>
                        <div className="h-[24px] bg-gray-100 rounded-full w-3/4 mb-2"></div>
                        <div className="h-[16px] bg-gray-100 rounded-full w-1/3 mb-2"></div>
                        <div className="h-[20px] bg-gray-100 rounded-full w-1/2 mb-2"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </LoadingItem>
          )}
          {!isLoading &&
            searchResults.map((result: StrapiMagazine, index) => (
              <div key={index}>
                <Link
                  prefetch={false}
                  href={result?.file?.url || '#'}
                  className="fade-in-up transition-all duration-300 ease-in-out"
                  target="_blank"
                >
                  <>
                    <div className="flex flex-col ">
                      <div className="my-4 flex flex-cols-2 gap-4 md:items-start w-full">
                        <div className="w-1/3 md:w-1/4 lg:w-1/5 ">
                          <div className="object-cover object-center w-full flex justify-center items-center ">
                            <LazyImage
                              className="aspect-[3/2] w-full rounded-sm"
                              src={result.thumbnail?.url}
                            />
                          </div>
                        </div>
                        <div className="text-gray-600 w-2/3 md:w-3/4 lg:w-4/5 flex items-start ">
                          <div className="w-full">
                            <div>
                              <h2
                                className={cn(
                                  'text-[24px] text-[#e31836] ',
                                  locale == 'en' ? 'font-[700]' : 'font-[500]'
                                )}
                              >
                                {result.title}
                              </h2>
                              <div className="text-[12px]">
                                <DateBlock date={result.posted_at} />
                              </div>
                            </div>
                            <div className="overflow-hidden">
                              <div className="text-gray-600 w-full line-clamp-1">
                                {parse(descriptionHandler(result.content))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                </Link>
              </div>
            ))}
        </div>
        {!isLoading && searchResults.length > 0 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
}
